import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(() => {
    try {
      // Read either key (legacy code paths use 'token', canonical is 'slidecon_token').
      return localStorage.getItem('slidecon_token') || localStorage.getItem('token') || null;
    } catch { return null; }
  });

  const persistToken = (t) => {
    setToken(t || null);
    try {
      if (t) {
        localStorage.setItem('slidecon_token', t);
        // Compat: many existing components read 'token' directly. Mirror.
        localStorage.setItem('token', t);
      } else {
        localStorage.removeItem('slidecon_token');
        localStorage.removeItem('token');
      }
    } catch { /* ignore quota */ }
  };

  // Check authentication status on mount
  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      setIsAuthenticated(true);
      if (response.data?.token) persistToken(response.data.token);
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Google Auth - REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Process Google Auth callback
  const processGoogleCallback = async (sessionId) => {
    try {
      const response = await axios.post(`${API}/auth/google-callback`, 
        { session_id: sessionId },
        { withCredentials: true }
      );
      setUser(response.data);
      setIsAuthenticated(true);
      if (response.data?.token) persistToken(response.data.token);
      return response.data;
    } catch (error) {
      console.error('Google auth callback error:', error);
      throw error;
    }
  };

  // Email signup
  const signupWithEmail = async (email, password, name) => {
    try {
      const response = await axios.post(`${API}/auth/email-signup`, 
        { email, password, name },
        { withCredentials: true }
      );
      setUser(response.data);
      setIsAuthenticated(true);
      if (response.data?.token) persistToken(response.data.token);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Email login
  const loginWithEmail = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/email-login`, 
        { email, password },
        { withCredentials: true }
      );
      setUser(response.data);
      setIsAuthenticated(true);
      if (response.data?.token) persistToken(response.data.token);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Set user type during onboarding
  const setUserType = async (userType, userSubcategory) => {
    try {
      const response = await axios.post(`${API}/auth/set-user-type`, 
        { user_type: userType, user_subcategory: userSubcategory },
        { withCredentials: true }
      );
      setUser(prev => ({
        ...prev,
        user_type: response.data.user_type,
        user_subcategory: response.data.user_subcategory,
        needs_onboarding: false
      }));
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Logout
  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      persistToken(null);
    }
  };

  // Update user data
  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      isAuthenticated,
      loginWithGoogle,
      processGoogleCallback,
      signupWithEmail,
      loginWithEmail,
      setUserType,
      logout,
      updateUser,
      checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
